/**
 * Profily hledání: lišta čipů nahoře v panelu + správce (jako u profilů
 * nastavení). Při ukládání si uživatel VYBÍRÁ, které části se mají
 * pamatovat; přepnutí aplikuje jen uložené sekce.
 */
import {
  createSearchProfile,
  moveSearchProfile,
  sortedSearchProfiles,
  type SearchProfile,
} from '../core/searchProfiles';
import {
  applySearchProfileToState,
  captureSearchProfileData,
  type Store,
} from '../state/store';
import { openDialog } from './dialogs';
import { el, clear } from './dom';
import { infoDot } from './help';
import { icon, iconOrEmoji, isIconName, type IconName } from './icons';

const SECTION_DEFS = [
  { key: 'strategy', label: 'Strategie hledání', desc: 'nejrychleji tam / nejpozdější odjezd / výchozí' },
  { key: 'sort', label: 'Řazení a filtry', desc: 'řadicí klíč, Bez auta, Bezbariérově, váhy doporučení' },
  { key: 'programs', label: 'Vlastní programy', desc: 'aktivace vlastního řazení, filtrů a hledání' },
  { key: 'modes', label: 'Výběr režimů', desc: 'které dopravní režimy jsou zapnuté' },
  { key: 'windows', label: 'Časová okna', desc: 'okna odjezdu a příjezdu (od–do / v ±)' },
] as const;

type SectionKey = (typeof SECTION_DEFS)[number]['key'];

function sectionsOf(p: SearchProfile): Record<SectionKey, boolean> {
  return {
    strategy: p.data.strategy !== undefined,
    sort: p.data.sort !== undefined,
    programs: p.data.programs !== undefined,
    modes: p.data.enabledModeIds !== undefined,
    windows: p.data.timeDep !== undefined || p.data.timeArr !== undefined,
  };
}

/** Lišta čipů profilů hledání (nahoře v panelu). */
export function searchProfileBar(store: Store, onChanged: () => void): HTMLElement {
  const bar = el('div', { className: 'sp-bar', role: 'group', 'aria-label': 'Profily hledání' });
  const render = (): void => {
    clear(bar);
    const s = store.get();
    for (const p of sortedSearchProfiles(s.searchProfiles).filter((x) => x.visible || x.id === s.activeSearchProfile)) {
      const active = p.id === s.activeSearchProfile;
      bar.append(
        el(
          'button',
          {
            className: `sp-chip${active ? ' sp-chip--on' : ''}`,
            type: 'button',
            'aria-pressed': String(active),
            title: p.description || p.name,
            style: `--sp-color:${p.color}`,
            onclick: () => {
              store.update(applySearchProfileToState(store.get(), p.id));
              onChanged();
              render();
            },
          },
          iconOrEmoji(isIconName(p.icon) ? p.icon : 'search', { size: 13 }),
          ` ${p.name}`,
        ),
      );
    }
    bar.append(
      el(
        'button',
        {
          className: 'sp-chip sp-chip--ghost',
          type: 'button',
          'aria-label': 'Spravovat profily hledání',
          title: 'Spravovat profily hledání',
          onclick: () => openSearchProfileManager(store, () => { onChanged(); render(); }),
        },
        icon('edit', { size: 13 }),
      ),
    );
  };
  render();
  store.subscribe(() => render());
  return bar;
}

export function openSearchProfileManager(store: Store, onChanged: () => void): void {
  const list = el('div', { className: 'profile-list' });
  const rerender = (): void => {
    clear(list);
    const s = store.get();
    for (const p of sortedSearchProfiles(s.searchProfiles)) {
      const stored = SECTION_DEFS.filter((d) => sectionsOf(p)[d.key]).map((d) => d.label);
      list.append(
        el(
          'div',
          { className: `profile-row${p.id === s.activeSearchProfile ? ' profile-row--active' : ''}`, style: `--p-color:${p.color}` },
          el('span', { className: 'profile-row__dot', 'aria-hidden': 'true' }),
          el('span', { className: 'profile-row__icon', 'aria-hidden': 'true' }, iconOrEmoji(isIconName(p.icon) ? p.icon : 'search')),
          el(
            'div',
            { className: 'profile-row__main' },
            el('div', { className: 'profile-row__name' }, p.name),
            el('div', { className: 'profile-row__desc' }, p.description || '—'),
            el('div', { className: 'profile-row__desc muted' }, stored.length > 0 ? `pamatuje: ${stored.join(', ')}` : 'zatím nic neukládá'),
          ),
          el(
            'div',
            { className: 'profile-row__actions' },
            btn('chevron-up', `Posunout ${p.name} výš`, () => { store.update({ searchProfiles: moveSearchProfile(store.get().searchProfiles, p.id, -1) }); rerender(); }),
            btn('chevron-down', `Posunout ${p.name} níž`, () => { store.update({ searchProfiles: moveSearchProfile(store.get().searchProfiles, p.id, 1) }); rerender(); }),
            btn(p.visible ? 'eye' : 'eye-off', p.visible ? `Skrýt ${p.name}` : `Zobrazit ${p.name}`, () => {
              patch(p.id, { visible: !p.visible });
              rerender();
            }),
            btn('edit', `Upravit ${p.name}`, () => openSearchProfileEditor(store, p.id, () => { onChanged(); rerender(); })),
            store.get().searchProfiles.length > 1
              ? btn('trash', `Smazat ${p.name}`, () => {
                  if (!window.confirm(`Smazat profil hledání „${p.name}"?`)) return;
                  const rest = store.get().searchProfiles.filter((x) => x.id !== p.id);
                  store.update({
                    searchProfiles: rest,
                    activeSearchProfile: store.get().activeSearchProfile === p.id ? rest[0]?.id ?? null : store.get().activeSearchProfile,
                  });
                  onChanged();
                  rerender();
                })
              : null,
          ),
        ),
      );
    }
  };
  const patch = (id: string, ch: Partial<SearchProfile>): void =>
    store.update({ searchProfiles: store.get().searchProfiles.map((x) => (x.id === id ? { ...x, ...ch } : x)) });
  const btn = (name: IconName, label: string, onclick: () => void): HTMLElement =>
    el('button', { className: 'btn btn--small btn--icon btn--ghost', 'aria-label': label, title: label, onclick }, icon(name));

  rerender();
  const dlg = openDialog({
    title: 'Profily hledání',
    body: el(
      'div',
      {},
      el('div', { className: 'muted', style: 'margin-bottom:8px' }, 'Profil hledání přepne najednou celé hledání. U každého profilu si vyberete, které části si má pamatovat.'),
      list,
      el('div', { style: 'margin-top:10px' }, el('button', {
        className: 'btn',
        onclick: () => {
          const p = createSearchProfile(store.get().searchProfiles);
          store.update({ searchProfiles: [...store.get().searchProfiles, p] });
          openSearchProfileEditor(store, p.id, () => { onChanged(); rerender(); });
          rerender();
        },
      }, icon('plus'), ' Nový profil hledání')),
    ),
    wide: true,
    footer: [el('button', { className: 'btn btn--primary', onclick: () => dlg.close() }, 'Hotovo')],
  });
}

export function openSearchProfileEditor(store: Store, id: string, onChanged: () => void): void {
  const get = (): SearchProfile | undefined => store.get().searchProfiles.find((x) => x.id === id);
  const p0 = get();
  if (!p0) return;
  const patch = (ch: Partial<SearchProfile>): void =>
    store.update({ searchProfiles: store.get().searchProfiles.map((x) => (x.id === id ? { ...x, ...ch } : x)) });

  const nameI = el('input', { className: 'input', value: p0.name, 'aria-label': 'Název', onchange: (e: Event) => patch({ name: (e.target as HTMLInputElement).value.trim() || p0.name }) });
  const descI = el('input', { className: 'input', value: p0.description, placeholder: 'Krátký popis', 'aria-label': 'Popis', onchange: (e: Event) => patch({ description: (e.target as HTMLInputElement).value.trim() }) });
  const colorI = el('input', { type: 'color', className: 'input', style: 'height:36px;padding:3px;width:64px', value: /^#[0-9a-f]{6}$/i.test(p0.color) ? p0.color : '#3b82f6', 'aria-label': 'Barva', onchange: (e: Event) => patch({ color: (e.target as HTMLInputElement).value }) });

  // Výběr sekcí: co si profil pamatuje. Uložení sebere hodnoty ze SOUČASNÉHO stavu.
  const checks = new Map<SectionKey, HTMLInputElement>();
  const initial = sectionsOf(p0);
  const sectionRows = SECTION_DEFS.map((d) => {
    const c = el('input', { type: 'checkbox', checked: initial[d.key] }) as HTMLInputElement;
    checks.set(d.key, c);
    return el('label', { className: 'switch', style: 'display:flex;margin-top:4px' }, c, el('span', {}, el('strong', {}, d.label), el('span', { className: 'muted' }, ` — ${d.desc}`)));
  });

  const status = el('div', { className: 'muted', role: 'status', style: 'margin-top:6px' });
  const saveFromCurrent = (): void => {
    const sections = {
      strategy: checks.get('strategy')!.checked,
      sort: checks.get('sort')!.checked,
      programs: checks.get('programs')!.checked,
      modes: checks.get('modes')!.checked,
      windows: checks.get('windows')!.checked,
    };
    patch({ data: captureSearchProfileData(store.get(), sections) });
    status.textContent = 'Uloženo ze současného stavu aplikace.';
    onChanged();
  };

  const dlg = openDialog({
    title: `Profil hledání: ${p0.name}`,
    body: el(
      'div',
      {},
      el('div', { className: 'field-grid' },
        el('div', {}, el('label', { className: 'label' }, 'Název'), nameI),
        el('div', {}, el('label', { className: 'label' }, 'Popis'), descI),
        el('div', {}, el('label', { className: 'label' }, 'Barva'), colorI),
      ),
      el('div', { className: 'card__title', style: 'margin-top:12px' }, 'Co si má profil pamatovat', infoDot(store, 'Nastavte aplikaci, jak potřebujete (strategie, řazení, režimy, okna…), zaškrtněte sekce a klikněte na „Uložit ze současného stavu". Přepnutí profilu pak aplikuje jen tyto části.')),
      ...sectionRows,
      el('div', { style: 'margin-top:10px' }, el('button', { className: 'btn btn--primary', onclick: saveFromCurrent }, icon('check', { size: 13 }), ' Uložit ze současného stavu')),
      status,
    ),
    wide: true,
    footer: [el('button', { className: 'btn', onclick: () => { onChanged(); dlg.close(); } }, 'Zavřít')],
  });
}
